import XIcon from "@redotech/redo-icons/x-close.svg";
import classnames from "classnames";
import {
  ForwardedRef,
  forwardRef,
  JSXElementConstructor,
  memo,
  ReactNode,
} from "react";
import CircleSpinner from "../../circle-spinner.svg";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { TextColorValue, TextWeightValue } from "../../theme/typography";
import { OptionalTooltip } from "../../tooltip/optional-tooltip";
import * as redoFilterGroupCss from "./redo-filter-group.module.css";

/**
 * Query and value are designed to be supplied with components like
 * `RedoFilterDropdownAnchor` and `FilterSection`.
 */
interface RedoFilterGroupProps {
  propertyName: string;
  Icon: JSXElementConstructor<any>;
  query: ReactNode;
  value: ReactNode;
  removeFilter?: () => void;
}

export const RedoFilterGroup = memo(function RedoFilterGroup({
  propertyName,
  Icon,
  query,
  value,
  removeFilter,
}: RedoFilterGroupProps) {
  return (
    <Flex
      className={redoFilterGroupCss.boxItems}
      gap="none"
      textColor="primary"
    >
      <Flex align="center" px="md" py="xxs">
        <Flex className={redoFilterGroupCss.iconWrapper}>
          <Icon className={redoFilterGroupCss.icon} />
        </Flex>
        <Text fontSize="sm" fontWeight="medium">
          {propertyName}
        </Text>
      </Flex>

      {query}
      {value}
      {removeFilter && (
        <button className={redoFilterGroupCss.button} onClick={removeFilter}>
          <Flex className={redoFilterGroupCss.iconWrapper}>
            <XIcon />
          </Flex>
        </button>
      )}
    </Flex>
  );
});

/**
 * Forward the ref, and supply it to a dropdown that applies the on click
 */
export const RedoFilterDropdownAnchor = memo(
  forwardRef(function RedoFilterGroupButton(
    {
      text,
      color = "primary",
      weight,
      tooltip,
      onClick,
      IconTrailing,
      className,
    }: {
      text: string;
      color?: TextColorValue;
      weight?: TextWeightValue;
      tooltip?: ReactNode;
      onClick?(): void;
      IconTrailing?: JSXElementConstructor<any>;
      className?: string;
    },
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    return (
      <OptionalTooltip
        meetsCondition={!!tooltip}
        tooltipProps={{ title: tooltip, placement: "top" }}
      >
        <button
          className={classnames(redoFilterGroupCss.button, className)}
          onClick={onClick}
          ref={ref}
        >
          <Flex gap="sm" pt={IconTrailing ? "xs" : undefined}>
            <Text fontSize="sm" fontWeight={weight} textColor={color}>
              {text}
            </Text>
            {IconTrailing && (
              <div
                className={classnames(
                  redoFilterGroupCss.iconWrapper,
                  redoFilterGroupCss.iconSecondary,
                )}
              >
                <IconTrailing />
              </div>
            )}
          </Flex>
        </button>
      </OptionalTooltip>
    );
  }),
);

export const RedoFilterSection = memo(function FilterSection({
  text,
  color = "primary",
  weight,
  pending = false,
}: {
  text?: string;
  color?: TextColorValue;
  weight?: TextWeightValue;
  pending?: boolean;
}) {
  return (
    <Flex align="center" px="md" py="xxs">
      {pending && (
        <Flex className={redoFilterGroupCss.iconWrapper}>
          <CircleSpinner />
        </Flex>
      )}
      {text && (
        <Text fontSize="sm" fontWeight={weight} textColor={color}>
          {text}
        </Text>
      )}
    </Flex>
  );
});
